import * as v from 'valibot';

export const VIEW_PREFERENCES_VERSION = 1;

/** The list surfaces that carry per-user view preferences (v1). */
export type ViewPreferenceContext
  = | 'team-issues'
    | 'team-sprints'
  // A saved view's own display configuration, keyed by the view id.
    | 'saved-view-issues';

// `backlog` is the team's Backlog tab (IssueBacklogView). The client stays
// lenient (see `blobSchema` below) and lets each consumer treat a value it
// cannot render as "not this surface" / fall back to its own default.
export type ViewLayout = 'board' | 'table' | 'backlog';

// Unknown keys are stripped (not rejected): a row written by a future client
// with extra sections must still resolve its known sections here. The server
// is the strict side — it whitelists keys on write.
const blobSchema = v.object({
  v: v.literal(VIEW_PREFERENCES_VERSION),
  layout: v.optional(v.picklist(['board', 'table', 'backlog'])),
});

export type ViewPreferencesBlob = v.InferOutput<typeof blobSchema>;

/**
 * Parse a JSON-serialized preferences blob coming from the server. Mirrors the
 * `filter-url.ts` philosophy for untrusted payloads: anything invalid —
 * malformed JSON, wrong shape, unsupported version — yields `null` so the
 * caller falls back to defaults. Never throws; a corrupt row must not break
 * the page.
 */
export const parseViewPreferences = (serialized: unknown): ViewPreferencesBlob | null => {
  if (typeof serialized !== 'string' || serialized.length === 0) {
    return null;
  }

  let raw: unknown;

  try {
    raw = JSON.parse(serialized);
  } catch {
    return null;
  }

  const result = v.safeParse(blobSchema, raw);

  return result.success ? result.output : null;
};
